import React from 'react';
import type { Room, RoomMetrics } from '@/types';

interface RoomStatusCardProps {
  room: Room;
  metrics?: RoomMetrics;
  onToggle: (enabled: boolean) => void;
}

export default function RoomStatusCard({ room, metrics, onToggle }: RoomStatusCardProps) {
  // Health check thresholds: error rate < 5%, latency < 1000ms
  const hasHighErrorRate = metrics ? metrics.errorRate >= 0.05 : false;
  const hasHighLatency = metrics ? metrics.averageLatency >= 1000 : false;
  const isHealthy = !hasHighErrorRate && !hasHighLatency;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h3 style={styles.roomName}>{room.name}</h3>
          <span
            style={{
              ...styles.statusBadge,
              backgroundColor: room.isActive ? '#48bb78' : '#a0aec0',
            }}
          >
            {room.isActive ? 'Active' : 'Disabled'}
          </span>
        </div>

        <label style={styles.toggle}>
          <input
            type="checkbox"
            checked={room.isActive}
            onChange={(e) => onToggle(e.target.checked)}
            style={styles.toggleInput}
          />
          <span style={{
            ...styles.toggleSlider,
            backgroundColor: room.isActive ? '#48bb78' : '#cbd5e0',
          }}>
            <span style={{
              ...styles.toggleKnob,
              transform: room.isActive ? 'translateX(24px)' : 'translateX(0)',
            }} />
          </span>
        </label>
      </div>

      {room.description && (
        <p style={styles.description}>{room.description}</p>
      )}

      <div style={styles.metrics}>
        <div style={styles.metric}>
          <span style={styles.metricLabel}>Participants</span>
          <span style={styles.metricValue}>{room.participantCount}</span>
        </div>

        {metrics && (
          <>
            <div style={styles.metric}>
              <span style={styles.metricLabel}>Avg Latency</span>
              <span style={styles.metricValue}>{metrics.averageLatency}ms</span>
            </div>

            <div style={styles.metric}>
              <span style={styles.metricLabel}>Error Rate</span>
              <span style={styles.metricValue}>
                {(metrics.errorRate * 100).toFixed(1)}%
              </span>
            </div>

            <div style={styles.metric}>
              <span style={styles.metricLabel}>Uptime</span>
              <span style={styles.metricValue}>
                {formatUptime(metrics.uptime)}
              </span>
            </div>
          </>
        )}
      </div>

      <div style={styles.footer}>
        <div style={styles.languages}>
          <span style={styles.languageLabel}>All 137 Languages</span>
        </div>
      </div>

      {!isHealthy && (
        <div style={styles.warning}>
          {hasHighErrorRate && hasHighLatency
            ? 'High error rate and latency detected'
            : hasHighErrorRate
            ? `High error rate: ${((metrics?.errorRate || 0) * 100).toFixed(1)}%`
            : `High latency: ${metrics?.averageLatency || 0}ms`}
        </div>
      )}
    </div>
  );
}

function formatUptime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  roomName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    margin: 0,
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
  },
  toggle: {
    position: 'relative',
    display: 'inline-block',
    width: '48px',
    height: '24px',
    cursor: 'pointer',
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleSlider: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#cbd5e0',
    borderRadius: '24px',
    transition: 'background-color 0.3s',
  },
  toggleKnob: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    transition: 'transform 0.3s',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
  },
  description: {
    fontSize: '14px',
    color: '#718096',
    margin: '0 0 16px 0',
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  metricLabel: {
    fontSize: '12px',
    color: '#718096',
    fontWeight: '500',
  },
  metricValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2d3748',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  languages: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  languageLabel: {
    fontSize: '12px',
    color: '#718096',
    fontWeight: '500',
  },
  languageBadge: {
    padding: '2px 8px',
    backgroundColor: '#e6fffa',
    color: '#234e52',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '600',
  },
  audioIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pulse: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#48bb78',
    animation: 'pulse 2s infinite',
  },
  audioText: {
    fontSize: '12px',
    color: '#48bb78',
    fontWeight: '600',
  },
  warning: {
    marginTop: '12px',
    padding: '8px 12px',
    backgroundColor: '#fef5e7',
    border: '1px solid #f7dc6f',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#b7791f',
  },
};
